import React, { useCallback, useRef, useState } from 'react'
import {
    Box,
    CircularProgress,
    IconButton,
    LinearProgress,
    Typography,
} from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import DescriptionIcon from '@material-ui/icons/Description'
import GetAppIcon from '@material-ui/icons/GetApp'
import WarningIcon from '@material-ui/icons/Warning'
import clsx from 'clsx'
import { toast } from 'react-toastify'

import {
    useVoiceAgentDocuments,
    fetchDocumentDetail,
    downloadDocumentFile,
} from '../../../hooks/useVoiceAgentDocuments'
import { VoiceAgentDocumentDetail } from '../../../models/VoiceAgent'
import {
    ALLOWED_DOC_EXTS,
    MAX_DOC_BYTES,
    MAX_DOC_FILES,
    MAX_DOC_CHARS,
    formatByteSize,
} from '../constants'
import { useVoiceAgentStyles } from '../useVoiceAgentStyles'

interface DocumentsSectionProps {
    totalChars: number
    docCount: number
    hideHeader?: boolean
}

const validateFile = (file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ALLOWED_DOC_EXTS.includes(ext)) {
        if (ext === '.doc') {
            return 'Legacy .doc format is not supported. Please convert to .docx and re-upload.'
        }
        return `Unsupported file type "${ext}". Allowed: ${ALLOWED_DOC_EXTS.join(
            ', ',
        )}.`
    }
    if (file.size > MAX_DOC_BYTES) {
        return `File too large (${formatByteSize(file.size)}). Maximum is 5 MB.`
    }
    return null
}

export const DocumentsSection: React.FC<DocumentsSectionProps> = ({
    totalChars,
    docCount,
    hideHeader = false,
}) => {
    const classes = useVoiceAgentStyles()
    const { documents, isLoading, upload, remove } = useVoiceAgentDocuments()
    const fileRef = useRef<HTMLInputElement>(null)
    const [dragging, setDragging] = useState(false)
    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [preview, setPreview] = useState<VoiceAgentDocumentDetail | null>(
        null,
    )
    const [previewLoading, setPreviewLoading] = useState(false)
    const [downloading, setDownloading] = useState<number | null>(null)

    const charTotal =
        documents.length > 0
            ? documents.reduce((sum, d) => sum + d.char_count, 0)
            : totalChars
    const atFileLimit = (documents.length || docCount) >= MAX_DOC_FILES
    const atCharLimit = charTotal >= MAX_DOC_CHARS
    const uploadDisabled = atFileLimit || atCharLimit || upload.isLoading

    const handleFiles = useCallback(
        (files: FileList | File[]) => {
            const file = files[0]
            if (!file) return
            const err = validateFile(file)
            if (err) {
                toast.error(err)
                return
            }
            upload.mutate(file)
        },
        [upload],
    )

    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault()
            setDragging(false)
            if (uploadDisabled) return
            handleFiles(e.dataTransfer.files)
        },
        [handleFiles, uploadDisabled],
    )

    const handleRowClick = async (id: number) => {
        if (expandedId === id) {
            setExpandedId(null)
            setPreview(null)
            return
        }
        setExpandedId(id)
        setPreviewLoading(true)
        try {
            const detail = await fetchDocumentDetail(id)
            setPreview(detail)
        } catch {
            toast.error('Could not load preview')
            setExpandedId(null)
        } finally {
            setPreviewLoading(false)
        }
    }

    const handleDelete = (e: React.MouseEvent, id: number) => {
        e.stopPropagation()
        if (expandedId === id) {
            setExpandedId(null)
            setPreview(null)
        }
        remove.mutate(id)
    }

    const budgetPct = Math.min((charTotal / MAX_DOC_CHARS) * 100, 100)

    return (
        <Box className={classes.docSection}>
            {!hideHeader && (
                <>
                    <Typography className={classes.fieldSectionTitle}>
                        Reference documents
                    </Typography>
                    <Typography className={classes.fieldSectionDesc}>
                        Upload files the agent can reference when answering
                        caller questions. Text is extracted and included in the
                        agent&apos;s context. Do not upload rent sheets or pet
                        addenda — those already come from the current leasing
                        season.
                    </Typography>
                </>
            )}

            <Box className={classes.docWarning}>
                <WarningIcon
                    style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}
                />
                <span>
                    These files are spoken on live calls. Do not upload internal
                    notes. Do not upload rent sheets or pet addenda — those
                    already come from the current leasing season. If a document
                    contradicts the structured property fields (parking,
                    laundry, etc.) or the leasing season data, the structured
                    fields and season data take priority.
                </span>
            </Box>

            <Box className={classes.docBudgetBar}>
                <Box className={classes.docBudgetLabel}>
                    <span>
                        {documents.length || docCount} file
                        {(documents.length || docCount) !== 1 ? 's' : ''} ·{' '}
                        {charTotal.toLocaleString()} /{' '}
                        {MAX_DOC_CHARS.toLocaleString()} chars
                    </span>
                    <span>{Math.round(budgetPct)}%</span>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={budgetPct}
                    color={budgetPct >= 90 ? 'secondary' : 'primary'}
                />
            </Box>

            {/* Upload drop zone */}
            <Box
                className={clsx(
                    classes.docDropZone,
                    dragging && classes.docDropZoneActive,
                    uploadDisabled && classes.docDropZoneDisabled,
                )}
                onDragOver={(e) => {
                    e.preventDefault()
                    if (!uploadDisabled) setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => {
                    if (!uploadDisabled) fileRef.current?.click()
                }}
            >
                <input
                    ref={fileRef}
                    type="file"
                    accept={ALLOWED_DOC_EXTS.join(',')}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        if (e.target.files) handleFiles(e.target.files)
                        e.target.value = ''
                    }}
                />
                {upload.isLoading ? (
                    <CircularProgress size={24} />
                ) : (
                    <>
                        <CloudUploadIcon
                            style={{ fontSize: 28, opacity: 0.5 }}
                        />
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ marginTop: 4 }}
                        >
                            {atFileLimit
                                ? `File limit reached (${MAX_DOC_FILES})`
                                : atCharLimit
                                ? 'Character budget full'
                                : 'Drop a file or click to upload'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {ALLOWED_DOC_EXTS.join(', ')} · max 5 MB
                        </Typography>
                    </>
                )}
            </Box>

            {/* File list */}
            {isLoading ? (
                <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={24} />
                </Box>
            ) : documents.length === 0 ? (
                <Typography className={classes.docEmpty}>
                    No reference documents yet. Upload community rules or an
                    amenity brochure.
                </Typography>
            ) : (
                <Box className={classes.docList}>
                    {documents.map((doc) => (
                        <Box key={doc.id}>
                            <Box
                                className={clsx(
                                    classes.docRow,
                                    expandedId === doc.id &&
                                        classes.docRowSelected,
                                )}
                                onClick={() => handleRowClick(doc.id)}
                            >
                                <Box className={classes.docFileIcon}>
                                    <DescriptionIcon style={{ fontSize: 18 }} />
                                </Box>
                                <Box className={classes.docFileMeta}>
                                    <Typography className={classes.docFileName}>
                                        {doc.filename}
                                    </Typography>
                                    <Typography className={classes.docFileSub}>
                                        {formatByteSize(doc.byte_size)} ·{' '}
                                        {doc.char_count.toLocaleString()} chars
                                        ·{' '}
                                        {new Date(
                                            doc.created_at,
                                        ).toLocaleDateString()}
                                    </Typography>
                                </Box>
                                <IconButton
                                    size="small"
                                    disabled={downloading === doc.id}
                                    onClick={async (e) => {
                                        e.stopPropagation()
                                        setDownloading(doc.id)
                                        try {
                                            await downloadDocumentFile(
                                                doc.id,
                                                doc.filename,
                                            )
                                        } catch {
                                            toast.error(
                                                'Failed to download file',
                                            )
                                        } finally {
                                            setDownloading(null)
                                        }
                                    }}
                                    aria-label="Download original"
                                >
                                    {downloading === doc.id ? (
                                        <CircularProgress size={14} />
                                    ) : (
                                        <GetAppIcon style={{ fontSize: 16 }} />
                                    )}
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={(e) => handleDelete(e, doc.id)}
                                    disabled={remove.isLoading}
                                    aria-label="Delete document"
                                >
                                    <DeleteOutlineIcon
                                        style={{ fontSize: 16 }}
                                    />
                                </IconButton>
                            </Box>
                            {expandedId === doc.id && (
                                <Box className={classes.docPreview}>
                                    {previewLoading ? (
                                        <CircularProgress size={16} />
                                    ) : preview?.text_preview ? (
                                        preview.text_preview
                                    ) : (
                                        <em>No text preview available.</em>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    )
}
